import { faClose, faEdit, faSave, faSyncAlt, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@mui/material';
import { type JSX, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';

import { SharedTPRMApi } from 'Api/TPRM/TPRMApi';
import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { ModalHeader } from 'Components/Modal/ModalHeader';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE } from 'Config/Icons';
import { isForbiddenResponseError } from 'Helpers/Auth/ResponseUtil';
import { getUserNameFromSubject } from 'Helpers/UserUtils';
import { DDQDiscussionDraft, ThirdPartyService } from 'Models/TPRM';
import { UserResponse } from 'Models/User';

import styles from './DDQDiscussionModal.module.css';

type DDQDiscussionModalBaseProps = {
    ddqApi: SharedTPRMApi;
    hideModal: () => void;
    service: ThirdPartyService;
    onDDQDiscussionDraftUpdated: () => Promise<void>;
    onDDQDiscussionSubmitted: () => Promise<void>;
    setTprmAccessDenied: (value: boolean) => void;
    discussionDrafts: DDQDiscussionDraft[];
};

export type DDQDiscussionModalThirdPartyProps = DDQDiscussionModalBaseProps & {
    isThirdParty: true;
    users?: never;
};

export type DDQDiscussionModalClientProps = DDQDiscussionModalBaseProps & {
    isThirdParty?: false | undefined;
    users: UserResponse[];
};

export type DDQDiscussionModalProps = DDQDiscussionModalThirdPartyProps | DDQDiscussionModalClientProps;
interface FormState {
    isCreatingDiscussionDraft?: boolean;
    isSubmitting?: boolean;
    isDeleting?: boolean;
    isEditing?: boolean;
    successMessage?: string;
    failureMessage?: string;
}

export const DDQDiscussionModal = (props: DDQDiscussionModalProps): JSX.Element => {
    const [newDiscussion, setNewDiscussion] = useState<string>('');
    const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);
    const [selectedDiscussionIdForEdit, setSelectedDiscussionIdForEdit] = useState<string | null>(null);
    const [selectedDiscussionIdForDelete, setSelectedDiscussionIdForDelete] = useState<string | null>(null);

    const [editedDiscussionText, setEditedDiscussionText] = useState<string>('');

    const [formState, setFormState] = useState<FormState>({});

    const handleCreateDiscussionDraft = async (): Promise<void> => {
        try {
            setFormState({ isCreatingDiscussionDraft: true });
            if (newDiscussion !== '' && newDiscussion !== undefined) {
                await props.ddqApi.createQuestionnaireDiscussionDraft(props.service.vendor_id, props.service.id, {
                    comment: newDiscussion,
                });
            }
            setNewDiscussion('');
            await props.onDDQDiscussionDraftUpdated();
            setFormState({ successMessage: 'Comment saved.', isCreatingDiscussionDraft: false });
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                props.setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    };
    const handleEditDiscussion = async (): Promise<void> => {
        try {
            setFormState({ isEditing: true });
            if (editedDiscussionText !== '' && editedDiscussionText !== undefined && selectedDiscussionIdForEdit !== null) {
                await props.ddqApi.updateQuestionnaireDiscussionDraft(props.service.vendor_id, props.service.id, selectedDiscussionIdForEdit, {
                    comment: editedDiscussionText,
                });
            }
            await props.onDDQDiscussionDraftUpdated();
            setEditedDiscussionText('');
            setSelectedDiscussionIdForEdit(null);
            setFormState({ successMessage: 'Comment updated.', isEditing: false });
        } catch (error) {
            console.error('error', error);
            if (isForbiddenResponseError(error)) {
                props.setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        try {
            setFormState({ isSubmitting: true });
            await props.ddqApi.submitQuestionnaireDiscussion(props.service.vendor_id, props.service.id, {
                discussion_ids: selectedCheckboxes,
            });
            await props.onDDQDiscussionSubmitted();
            await setFormState({ successMessage: props.isThirdParty ? 'Comments have been successfully sent to the service contact.' : 'Comments have been successfully sent to the third-party contact.', isSubmitting: false });
            setSelectedCheckboxes([]);
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                props.setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    };

    const handleDelete = async (discussionId: string): Promise<void> => {
        try {
            setFormState({ isDeleting: true });
            setSelectedDiscussionIdForDelete(discussionId);
            await props.ddqApi.deleteQuestionnaireDiscussionDraft(props.service.vendor_id, props.service.id, discussionId);
            await props.onDDQDiscussionDraftUpdated();
            setSelectedDiscussionIdForDelete(null);
            setFormState({ successMessage: 'Comment deleted.', isDeleting: false });
        } catch (error) {
            if (isForbiddenResponseError(error)) {
                props.setTprmAccessDenied(true);
            } else {
                handleRequestError(error);
            }
        }
    };

    const handleRequestError = (error: Error) => {
        setFormState({ failureMessage: error.message, isCreatingDiscussionDraft: false, isDeleting: false, isSubmitting: false, isEditing: false });
    };

    const handleCheckboxChange = (discussionId: string) => {
        setSelectedCheckboxes((prevSelected) => (prevSelected.includes(discussionId) ? prevSelected.filter((id) => id !== discussionId) : [...prevSelected, discussionId]));
    };

    return (
        <Modal show onHide={props.hideModal} size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
            <Modal.Body className={'modalFromBody'}>
                {formState.successMessage && <Alert variant="success">{formState.successMessage}</Alert>}
                {formState.failureMessage && <Alert variant="danger">{formState.failureMessage}</Alert>}
                <Form noValidate onSubmit={handleSubmit}>
                    <ModalHeader text="Manage Discussion Drafts" secondaryText={props.isThirdParty ? 'Create additional comments in this section. Once created, comments can be edited or deleted before submission. When ready, select and submit one or more comments. Any pending comments from other users will also appear here and will be available for submission.' : 'Create additional comments or requests for the third party in this section. Once created, comments can be edited or deleted before submission. When ready, select and submit one or more comments. Any pending comments from other users will also appear here and will be available for submission.'} />
                    <div className={styles.fieldContainer}>
                        <FormFieldTextArea handleChange={(event: React.FormEvent<HTMLInputElement>) => setNewDiscussion(event.currentTarget.value)} formFieldId="additionalQuestionnaireDiscussion" formFieldLabel="Comment" rows={3} required value={newDiscussion} disabled={formState.isEditing === true || formState.isDeleting === true || formState.isSubmitting === true || formState.isCreatingDiscussionDraft === true} />
                    </div>
                    <div className={'modalFormButtonContainer'}>
                        <Button variant="secondary" onClick={handleCreateDiscussionDraft} fontAwesomeImage={faSave} isLoading={formState.isCreatingDiscussionDraft === true} loadingText="Saving..." disabled={selectedDiscussionIdForEdit !== null || formState.isEditing === true || selectedDiscussionIdForDelete !== null || formState.isDeleting === true || formState.isSubmitting === true || newDiscussion === ''}>
                            Save New Comment
                        </Button>
                    </div>
                    <Text variant="Header4" noStyles>
                        Pending Comments
                    </Text>
                    <div className={styles.alignVertical}>
                        <hr />
                        {props.discussionDrafts.length > 0 ? (
                            props.discussionDrafts.map((discussion, index) => {
                                const isChecked = selectedCheckboxes.includes(discussion.discussion_id);

                                return (
                                    <>
                                        <div key={index}>
                                            {selectedDiscussionIdForEdit === discussion.discussion_id ? (
                                                <div className={styles.editDiscussion}>
                                                    <FormFieldTextArea handleChange={(event: React.FormEvent<HTMLInputElement>) => setEditedDiscussionText(event.currentTarget.value)} formFieldId="editDiscussion" formFieldLabel="Edit Comment" rows={3} required value={editedDiscussionText} disabled={formState.isEditing === true || formState.isDeleting === true || formState.isSubmitting === true || formState.isCreatingDiscussionDraft === true} />
                                                    <div className={'modalFormButtonContainer'}>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => {
                                                                setSelectedDiscussionIdForEdit(null);
                                                                setEditedDiscussionText('');
                                                            }}
                                                            fontAwesomeImage={faClose}
                                                            isLoading={formState.isCreatingDiscussionDraft === true}
                                                            loadingText="Discarding..."
                                                            disabled={formState.isEditing === true || formState.isDeleting === true || formState.isSubmitting === true || formState.isCreatingDiscussionDraft === true}
                                                        >
                                                            Discard Changes
                                                        </Button>
                                                        <Button variant="primary" isLoading={formState.isEditing === true} loadingText="Saving..." fontAwesomeImage={faSave} onClick={handleEditDiscussion} disabled={formState.isEditing === true || formState.isDeleting === true || formState.isSubmitting === true}>
                                                            Save Edited Comment
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className={styles.selectBox}>
                                                    <Checkbox color="default" checked={isChecked} disabled={selectedDiscussionIdForEdit !== null || formState.isEditing === true || selectedDiscussionIdForDelete !== null || formState.isDeleting === true || formState.isSubmitting === true || formState.isCreatingDiscussionDraft === true} onChange={() => handleCheckboxChange(discussion.discussion_id)} />
                                                    <div className={styles.ownerCommentHistoryContainer}>
                                                        <div className={styles.ownerCommentHistoryUserAndTimestamp}>
                                                            {props.isThirdParty ? <Text variant="Header4">{discussion.requested_by}</Text> : <Text variant="Header4">{getUserNameFromSubject(discussion.requested_by, props.users)}</Text>}
                                                            <Text>{discussion.comment}</Text>
                                                        </div>
                                                        <div className={styles.iconButtons}>
                                                            <IconButton
                                                                onClick={() => {
                                                                    setSelectedDiscussionIdForEdit(discussion.discussion_id);
                                                                    setEditedDiscussionText(discussion.comment);
                                                                }}
                                                                aria-label="edit"
                                                                fontAwesomeImage={faEdit}
                                                                disabled={selectedDiscussionIdForEdit !== null || formState.isEditing === true || selectedDiscussionIdForDelete !== null || formState.isDeleting === true || formState.isSubmitting === true || formState.isCreatingDiscussionDraft === true}
                                                            />
                                                            {selectedDiscussionIdForDelete === discussion.discussion_id ? (
                                                                <div className={styles.deletingIcon}>
                                                                    <FontAwesomeIcon icon={faSyncAlt} />
                                                                </div>
                                                            ) : (
                                                                <IconButton onClick={() => handleDelete(discussion.discussion_id)} aria-label="delete" fontAwesomeImage={faTrash} disabled={selectedDiscussionIdForEdit !== null || formState.isEditing === true || selectedDiscussionIdForDelete !== null || formState.isSubmitting === true || formState.isCreatingDiscussionDraft === true} />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <hr />
                                    </>
                                );
                            })
                        ) : (
                            <Text>No pending comments.</Text>
                        )}
                    </div>
                    <div className={styles.buttonContainer}>
                        <Button variant="secondary" onClick={props.hideModal} fontAwesomeImage={ICON_CLOSE} disabled={formState.isEditing === true || formState.isDeleting === true || formState.isCreatingDiscussionDraft === true || formState.isSubmitting === true}>
                            Close
                        </Button>
                        <div className={'modalFormButtonContainer'}>
                            <Button variant="submit" isLoading={formState.isSubmitting === true} loadingText="Submitting..." disabled={selectedDiscussionIdForEdit !== null || formState.isEditing === true || formState.isDeleting === true || formState.isCreatingDiscussionDraft === true || selectedCheckboxes.length === 0}>
                                Submit Selected Comments
                            </Button>
                        </div>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};
